// src/utils/exportUtils.js
import * as XLSX from 'xlsx';
import * as StyledXLSX from 'xlsx-js-style';
import { formatPositionsDisplay } from '../services/positionAPIs';

const formatDate = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateDDMMYYYY = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0'); // getMonth() is 0-based
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Exports the full attendance table (ALL view) to Excel
 * @param {Array} data - Full employee data (as used in the ALL table)
 * @param {Object} dateRange - { from, to }
 */
export const exportAttendanceToExcel = (data, dateRange) => {
  const worksheetData = data.map((employee) => ({
    'Employee Name': employee.employeeName || 'N/A',
    'Department': employee.department || 'N/A',
    'Position': employee.position || 'N/A',
    'Annual Leave Days': employee.leaveDays?.ANNUAL_LEAVE || 0,
    'Annual Leave Balance': employee.leaveBalances?.ANNUAL_LEAVE || 0,
    'Unpaid Leave Days': employee.leaveDays?.UNPAID_LEAVE || 0,
    'Sickness Records': employee.totalSicknessRecords || 0,
    'Sickness Hours': Number(employee.totalSicknessHours || 0).toFixed(1),
    'Lateness Records': employee.totalLatenessRecords || 0,
    // 'Lateness Hours': Number(employee.totalLatenessHours || 0).toFixed(1),
    'Personal Records': employee.totalPersonalRecords || 0,
    'Personal Hours': Number(employee.totalPersonalHours || 0).toFixed(1),
  }));

  const ws = XLSX.utils.json_to_sheet(worksheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Full Attendance Report');

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  // Resolve actual from/to dates
  const displayFrom = dateRange.from ? new Date(dateRange.from) : startOfMonth;
  const displayTo = dateRange.to ? new Date(dateRange.to) : endOfMonth;


  // Add summary sheet
  const summary = [
    ['Report Type', 'Full Attendance Report (All Types)'],
    ['Start Date', formatDateDDMMYYYY(displayFrom)],
    ['End Date', formatDateDDMMYYYY(displayTo)],
    ['Total Employees', data.length],
    ['Generated On', formatDateDDMMYYYY(new Date())]
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summary);
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  XLSX.writeFile(wb, `full-attendance-report.xlsx`);
};

/**
 * Exports the "All Records" attendance table to a styled Excel file
 * that mirrors the on-screen table colors (grouped colored headers + body).
 * @param {Array} data - Filtered employee data shown in the All Records table
 * @param {Object} dateRange - { from, to }
 */
export const exportAttendanceTableToExcel = (data, dateRange) => {
  if (!data || data.length === 0) return;

  const headerRow1 = [
    'Employee Name',
    'Annual Leave', '', '', '',
    'Sickness', '',
    'Lateness', '',
    'Personal', '',
  ];

  const headerRow2 = [
    '',
    'Annual vacation', 'Vacation Taken', 'Bal', 'Unpaid vacation',
    'Rec', 'Days',
    'Rec', 'Hrs',
    'Rec', 'Hrs',
  ];

  const bodyRows = data.map((employee) => [
    employee.employeeName || 'N/A',
    employee.annualVacation ||
      ((employee.leaveDays?.ANNUAL_LEAVE || 0) + (employee.leaveBalances?.ANNUAL_LEAVE || 0)),
    employee.leaveDays?.ANNUAL_LEAVE || 0,
    employee.leaveBalances?.ANNUAL_LEAVE || 0,
    employee.leaveDays?.UNPAID_LEAVE || 0,
    employee.totalSicknessRecords || 0,
    Number(employee.totalSicknessHours / 8 || 0).toFixed(1),
    employee.totalLatenessRecords || 0,
    Number(employee.totalLatenessHours || 0).toFixed(1),
    employee.totalPersonalRecords || 0,
    Number(employee.totalPersonalHours || 0).toFixed(1),
  ]);

  const ws = StyledXLSX.utils.aoa_to_sheet([headerRow1, headerRow2, ...bodyRows]);

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },   // Employee Name (vertical)
    { s: { r: 0, c: 1 }, e: { r: 0, c: 4 } },   // Annual Leave
    { s: { r: 0, c: 5 }, e: { r: 0, c: 6 } },   // Sickness
    { s: { r: 0, c: 7 }, e: { r: 0, c: 8 } },   // Lateness
    { s: { r: 0, c: 9 }, e: { r: 0, c: 10 } },  // Personal
  ];

  const COLORS = {
    employeeGroup: 'FAFAFA',
    annualGroup: 'BBDEFB',
    sicknessGroup: 'FFE0B2',
    latenessGroup: 'E1BEE7',
    personalGroup: 'C8E6C9',
    annualSub: 'E3F2FD',
    sicknessSub: 'FFF3E0',
    latenessSub: 'F3E5F5',
    personalSub: 'E8F5E9',
    annualBody: 'F7FBFF',
    sicknessBody: 'FFFAF3',
    latenessBody: 'FAF5FB',
    personalBody: 'F5FBF6',
  };

  const thinBorder = {
    top: { style: 'thin', color: { rgb: 'D0D0D0' } },
    bottom: { style: 'thin', color: { rgb: 'D0D0D0' } },
    left: { style: 'thin', color: { rgb: 'D0D0D0' } },
    right: { style: 'thin', color: { rgb: 'D0D0D0' } },
  };

  const buildStyle = (fill, { bold = false, align = 'center' } = {}) => {
    const style = {
      font: { name: 'Calibri', sz: 11, bold },
      alignment: { horizontal: align, vertical: 'middle', wrapText: true },
      border: thinBorder,
    };
    if (fill) style.fill = { fgColor: { rgb: fill } };
    return style;
  };

  const setCell = (r, c, style) => {
    const ref = StyledXLSX.utils.encode_cell({ r, c });
    if (!ws[ref]) ws[ref] = { t: 's', v: '' };
    ws[ref].s = style;
  };

  const styleRange = (row, cols, fill, opts) =>
    cols.forEach((c) => setCell(row, c, buildStyle(fill, opts)));

  const lastRow = 1 + data.length;

  // Row 0 — group headers
  styleRange(0, [0], COLORS.employeeGroup, { bold: true });
  styleRange(0, [1, 2, 3, 4], COLORS.annualGroup, { bold: true });
  styleRange(0, [5, 6], COLORS.sicknessGroup, { bold: true });
  styleRange(0, [7, 8], COLORS.latenessGroup, { bold: true });
  styleRange(0, [9, 10], COLORS.personalGroup, { bold: true });

  // Row 1 — sub headers
  styleRange(1, [0], COLORS.employeeGroup, { bold: true });
  styleRange(1, [1, 2, 3, 4], COLORS.annualSub, { bold: true });
  styleRange(1, [5, 6], COLORS.sicknessSub, { bold: true });
  styleRange(1, [7, 8], COLORS.latenessSub, { bold: true });
  styleRange(1, [9, 10], COLORS.personalSub, { bold: true });

  // Body rows
  for (let r = 2; r <= lastRow; r++) {
    setCell(r, 0, buildStyle(undefined, { align: 'left' }));
    styleRange(r, [1, 2, 3, 4], COLORS.annualBody);
    styleRange(r, [5, 6], COLORS.sicknessBody);
    styleRange(r, [7, 8], COLORS.latenessBody);
    styleRange(r, [9, 10], COLORS.personalBody);
  }

  // Column widths
  const allRows = [headerRow1, headerRow2, ...bodyRows];
  const colWidths = [];
  for (let r = 0; r < allRows.length; r++) {
    for (let c = 0; c <= 10; c++) {
      const value = allRows[r][c]?.toString() || '';
      const width = Math.min(30, Math.max(8, value.length + 3));
      if (!colWidths[c] || width > colWidths[c]) colWidths[c] = width;
    }
  }
  colWidths[0] = Math.max(colWidths[0] || 8, 25);
  ws['!cols'] = colWidths.map((w) => ({ wch: w }));

  const wb = StyledXLSX.utils.book_new();
  StyledXLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');

  // Summary sheet
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const displayFrom = dateRange?.from ? new Date(dateRange.from) : startOfMonth;
  const displayTo = dateRange?.to ? new Date(dateRange.to) : endOfMonth;

  const summary = [
    ['Report Type', 'Attendance Report (All Types)'],
    ['Start Date', formatDateDDMMYYYY(displayFrom)],
    ['End Date', formatDateDDMMYYYY(displayTo)],
    ['Total Employees', data.length],
    ['Generated On', formatDateDDMMYYYY(new Date())],
  ];
  const summaryWs = StyledXLSX.utils.aoa_to_sheet(summary);
  StyledXLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  StyledXLSX.writeFile(wb, 'attendance-report.xlsx');
};

/**
 * Exports the full attendance table to Word (.doc)
 * @param {Array} data - Full employee data
 * @param {Object} dateRange - { from, to }
 */
export const exportAttendanceToWord = (data, dateRange) => {
  const tableRows = data.map(emp => `
    <tr>
      <td>${emp.employeeName || 'N/A'}</td>
      <td>${emp.leaveDays?.ANNUAL_LEAVE || 0}</td>
      <td>${emp.leaveBalances?.ANNUAL_LEAVE || 0}</td>
      <td>${emp.leaveDays?.UNPAID_LEAVE || 0}</td>
      <td>${emp.totalSicknessRecords || 0}</td>
      <td>${Number(emp.totalSicknessHours || 0).toFixed(1)}</td>
      <td>${emp.totalLatenessRecords || 0}</td>
      <td>${emp.totalPersonalRecords || 0}</td>
      <td>${Number(emp.totalPersonalHours || 0).toFixed(1)}</td>
    </tr>
  `).join('');

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  // Resolve actual from/to dates
  const displayFrom = dateRange.from ? new Date(dateRange.from) : startOfMonth;
  const displayTo = dateRange.to ? new Date(dateRange.to) : endOfMonth;

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Full Attendance Report</title>
      <style>
        body { font-family: Arial, sans-serif; }
        .header { color: #2196F3; border-bottom: 2px solid #2196F3; padding-bottom: 10px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #2196F3; color: white; }
        tr:nth-child(even) { background-color: #f9f9f9; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Full Attendance Report</h1>
        <p>Period: ${formatDateDDMMYYYY(displayFrom)} 
           to ${formatDateDDMMYYYY(displayTo)}</p>
        <p>Generated: ${formatDateDDMMYYYY(new Date().toLocaleString())}</p>
      </div>
      <table>
        <thead>
          <tr>
            <th>Employee Name</th>
            <th>Annual Days</th>
            <th>Annual Bal</th>
            <th>Unpaid Days</th>
            <th>Sick Rec</th>
            <th>Sick Hrs</th>
            <th>Late Rec</th>
            <th>Personal Rec</th>
            <th>Personal Hrs</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'full-attendance-report.doc';
  link.click();
  URL.revokeObjectURL(url);
};